import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  EnvelopeSimple,
  MapPin,
  Robot,
  ShieldCheck,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import SEOHead from "@/lib/seo/SEOHead";
import GbpCandidatePicker from "../components/GbpCandidatePicker";

const SMS_CONSENT_TEXT_VERSION = "ranktrust_sms_consent_v8_2026_08_voluntary";
const STRATEGY_MEETING_URL = "https://calendly.com/intentbrain/ranktrust-io";
const CHALLENGES = [
  "Getting more qualified leads and phone calls", "Showing up higher on Google Maps",
  "Getting found in ChatGPT and other AI searches", "Turning more website visitors into customers",
  "Following up with leads before they go elsewhere", "Getting more customer reviews and referrals",
  "Reducing marketing costs", "Automating repetitive work", "Something else",
];

const QUESTIONS = [
  {
    key: "local_visibility",
    question: "Are customers consistently finding your business when they search locally?",
    answers: ["Yes, always", "Sometimes / Unsure", "No, we are buried"],
  },
  {
    key: "website_answers",
    question: "Does your website answer the questions customers need answered before choosing you?",
    answers: ["Yes, thoroughly", "Somewhat / Barely", "No / Unsure"],
  },
  {
    key: "reviews_trust",
    question: "Are your reviews helping customers choose you over competitors?",
    answers: ["Yes, we dominate", "They help, but we need more", "No, or we have negative reviews"],
  },
  {
    key: "lead_response",
    question: "When a new lead comes in, does someone usually respond within minutes?",
    answers: ["Yes, instantly", "Usually within an hour", "No, it takes hours or days"],
  },
  {
    key: "estimate_follow_up",
    question: "Do estimates receive consistent follow-up until the customer makes a decision?",
    answers: ["Yes, automated/tracked", "Sometimes, manually", "No, we lose track"],
  },
  {
    key: "ai_visibility",
    question: "Do you know whether AI systems recommend your business when customers ask who to hire?",
    answers: ["Yes, we monitor it", "We think so", "No, no idea"],
  },
];

function statusFromAnswer(answer, good, middle) {
  if (!answer) return { label: "Monitor", tone: "zinc" };
  if (answer === good) return { label: "Strong", tone: "emerald" };
  if (answer === middle) return { label: "Opportunity detected", tone: "amber" };
  return { label: "High priority", tone: "rose" };
}

function toneClasses(tone) {
  if (tone === "emerald") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (tone === "amber") return "border-amber-200 bg-amber-50 text-amber-800";
  if (tone === "rose") return "border-rose-200 bg-rose-50 text-rose-800";
  if (tone === "blue") return "border-blue-200 bg-blue-50 text-blue-800";
  return "border-zinc-200 bg-zinc-50 text-zinc-700";
}

function signalStatus(result) {
  const hasMapFinding =
    typeof result?.map_rank === "number" ||
    typeof result?.map_position === "number" ||
    !!result?.maps_opportunity ||
    !!result?.highest_impact;

  const hasWebsiteFinding =
    !!result?.answer_visibility ||
    typeof result?.website_score === "number" ||
    !!result?.website_analysis;

  const hasAiFinding =
    typeof result?.overall_mention_rate === "number" ||
    !!result?.engines ||
    !!result?.ai_visibility;

  return { hasMapFinding, hasWebsiteFinding, hasAiFinding };
}

function observedGoogleFinding(result) {
  if (!result) return null;
  if (result.serpapi_unavailable) {
    return { key: "google", label: "Google Visibility", source: "RankTrust observed", value: "Data unavailable", detail: "Live Google Maps data could not be retrieved for this run.", tone: "zinc", severity: 0, observed: true };
  }
  if (result.verification_status === "unable_to_verify") {
    return { key: "google", label: "Google Visibility", source: "RankTrust observed", value: "Needs verification", detail: "RankTrust could not verify the Google Business Profile, so a reliable Maps position was not assigned.", tone: "amber", severity: 1, observed: true };
  }

  const rank = [result.map_position, result.map_rank, result.current_rank].find((v) => typeof v === "number");
  const market = [result.city, result.state].filter(Boolean).join(", ");
  const keyword = result.keyword ? `“${result.keyword}”` : "this search";

  if (result.canonical_only || result.map_matched === false) {
    return {
      key: "google",
      label: "Google Visibility",
      source: "RankTrust observed",
      value: "Outside top 40",
      detail: `Your business is not appearing in the top 40 Google Maps results for ${keyword}${market ? ` in ${market}` : ""}.`,
      tone: "rose",
      severity: 3,
      observed: true,
    };
  }

  if (typeof rank === "number") {
    const severity = rank <= 3 ? 0 : rank <= 10 ? 1 : 3;
    const tone = rank <= 3 ? "emerald" : rank <= 10 ? "amber" : "rose";
    const consequence =
      result.maps_opportunity?.evidence ||
      result.share_of_attention_copy ||
      (rank <= 3
        ? "Your business is currently appearing in the leading local results for this search."
        : "Customers have multiple opportunities to encounter competitors before reaching your business.");
    return {
      key: "google",
      label: "Google Visibility",
      source: "RankTrust observed",
      value: `#${rank}`,
      detail: `RankTrust measured your business at #${rank} for ${keyword}${market ? ` in ${market}` : ""}. ${consequence}`,
      tone,
      severity,
      observed: true,
    };
  }

  if (result.maps_opportunity?.headline || result.maps_opportunity?.evidence) {
    return {
      key: "google",
      label: "Google Visibility",
      source: "RankTrust observed",
      value: "Finding detected",
      detail: [result.maps_opportunity.headline, result.maps_opportunity.evidence].filter(Boolean).join(" "),
      tone: "amber",
      severity: 2,
      observed: true,
    };
  }
  return null;
}

function observedWebsiteFinding(result) {
  if (!result) return null;
  const analysis = result.answer_visibility || result.website_analysis;
  if (analysis?.analysis_status === "complete") {
    const readiness = analysis.answer_readiness || {};
    const missing = Number(readiness.missing_answers || 0);
    const weak = Number(readiness.weak_answers || 0);
    const strong = Number(readiness.strong_answers || 0);
    const topOpportunity = analysis.opportunities?.[0];
    const missingQuestion = (analysis.question_findings || []).find((item) => item?.coverage === "missing" && item?.question);
    const weakQuestion = (analysis.question_findings || []).find((item) => item?.coverage === "weak" && item?.question);
    const severity = missing > 0 ? 3 : weak > 0 ? 2 : 0;
    const tone = missing > 0 ? "rose" : weak > 0 ? "amber" : "emerald";
    const value = missing > 0 ? `${missing} answer${missing === 1 ? "" : "s"} missing` : weak > 0 ? `${weak} answer${weak === 1 ? "" : "s"} need work` : "Strong answer coverage";
    let detail;
    if (missingQuestion) {
      const remaining = Math.max(0, missing - 1);
      detail = `One example: “${missingQuestion.question}” is not clearly answered on the relevant public website content.${remaining ? ` RankTrust identified ${remaining} additional answer gap${remaining === 1 ? "" : "s"}.` : ""}`;
    } else if (weakQuestion) {
      detail = `One example: “${weakQuestion.question}” is only partially answered on the relevant public website content.`;
    } else {
      detail =
        topOpportunity?.finding ||
        analysis.summary ||
        `Website answer readiness found ${strong} strong, ${weak} weak, and ${missing} missing customer answers.`;
    }
    return { key: "website", label: "Website & Answers", source: "RankTrust observed", value, detail, tone, severity, observed: true };
  }

  if (typeof result.website_score === "number") {
    const score = result.website_score;
    const severity = score < 50 ? 3 : score < 75 ? 2 : 0;
    const tone = score < 50 ? "rose" : score < 75 ? "amber" : "emerald";
    return {
      key: "website",
      label: "Website & Answers",
      source: "RankTrust observed",
      value: `${score}/100`,
      detail: `RankTrust measured the website at ${score}/100 on the available website signals for this assessment.`,
      tone,
      severity,
      observed: true,
    };
  }

  if (analysis?.summary) {
    const unavailable = analysis.analysis_status && analysis.analysis_status !== "complete";
    return {
      key: "website",
      label: "Website & Answers",
      source: "RankTrust observed",
      value: unavailable ? "Analysis incomplete" : "Finding available",
      detail: unavailable
        ? "Website analysis could not be completed reliably during this run. RankTrust did not infer missing answers from incomplete website data. This area should be rechecked before making a recommendation."
        : analysis.summary,
      tone: "amber",
      severity: unavailable ? 0 : 1,
      observed: true,
    };
  }
  return null;
}

function engineDisplayName(name) {
  const normalized = String(name || "").toLowerCase();
  if (normalized === "chatgpt") return "ChatGPT";
  if (normalized === "perplexity") return "Perplexity";
  if (normalized === "claude") return "Claude";
  if (normalized === "gemini") return "Gemini";
  return String(name || "").replace(/^./, (ch) => ch.toUpperCase());
}

function observedAiFinding(result) {
  if (!result) return null;
  const measured = Object.entries(result.engines || {})
    .filter(([, data]) => data && !data.mocked && typeof data.mention_rate === "number");

  if (!measured.length) return null;

  const rates = measured.map(([, data]) => Number(data.mention_rate)).filter(Number.isFinite);
  if (!rates.length) return null;

  const rate = Math.round((rates.reduce((sum, value) => sum + value, 0) / rates.length) * 10) / 10;
  const severity = rate < 30 ? 3 : rate < 60 ? 2 : 0;
  const tone = rate < 30 ? "rose" : rate < 60 ? "amber" : "emerald";
  const engines = measured.map(([name, data]) => `${engineDisplayName(name)} ${data.mention_rate}%`);
  const basis = measured.length === 1
    ? `Based on ${engineDisplayName(measured[0][0])} only in this assessment.`
    : `Based on ${measured.map(([name]) => engineDisplayName(name)).join(", ")} in this assessment.`;

  const detail = measured.length === 1
    ? `${rate === 0 ? "Your business was not named in any" : `Your business was named in ${rate}%`} of the ${engineDisplayName(measured[0][0])} recommendation tests completed for this assessment. Measured provider: ${engines[0]}.`
    : `Across the AI providers measured in this assessment, your business was named ${rate}% of the time. Provider breakdown: ${engines.join(" · ")}.`;

  return {
    key: "ai",
    label: "AI Answers",
    source: "RankTrust observed",
    value: `${rate}% mention rate`,
    detail,
    tone,
    severity,
    observed: true,
  };
}

function observedReviewFinding(result) {
  if (!result) return null;
  const hasRating = typeof result.map_rating === "number";
  const hasCount = typeof result.map_review_count === "number";
  if (!hasRating && !hasCount) return null;
  const parts = [];
  if (hasRating) parts.push(`${result.map_rating.toFixed(1)}★ rating`);
  if (hasCount) parts.push(`${result.map_review_count.toLocaleString()} Google reviews`);
  return {
    key: "reviews-observed",
    label: "Google Review Signals",
    source: "RankTrust observed",
    value: hasRating ? `${result.map_rating.toFixed(1)}★` : `${result.map_review_count.toLocaleString()} reviews`,
    detail: `Your Google Business Profile currently shows ${parts.join(" and ")}. This is a public trust signal, not a complete measure of customer confidence.`,
    tone: hasRating && result.map_rating >= 4.5 ? "emerald" : "blue",
    severity: hasRating && result.map_rating < 4.2 ? 2 : 0,
    observed: true,
  };
}

function contextFinding(key, label, status, detail) {
  return {
    key,
    label,
    source: "What you told us",
    value: status.label,
    detail,
    tone: status.tone,
    severity: status.label === "High priority" ? 4 : status.label === "Opportunity detected" ? 2 : status.label === "Strong" ? 0 : 1,
    observed: false,
  };
}

function leadResponseDetail(answer) {
  if (answer === "Yes, instantly") {
    return "You told us new inquiries are answered almost immediately. That response speed is a strength worth protecting as lead volume grows.";
  }
  if (answer === "Usually within an hour") {
    return "You told us new inquiries are usually answered within an hour. In urgent home-service situations, customers often keep searching until someone responds. Even a few minutes can give another contractor the opportunity to answer first and win the conversation.";
  }
  return "You told us new inquiries can take hours or days to receive a response. In urgent home-service situations, customers often keep searching until someone responds, creating a substantial opportunity for a faster-responding competitor to win the conversation first.";
}

function estimateFollowUpDetail(answer) {
  if (answer === "Yes, automated/tracked") {
    return "You told us estimates are consistently tracked and followed up. That process is a strength because interested prospects are less likely to disappear simply because follow-up was missed.";
  }
  if (answer === "Sometimes, manually") {
    return "You told us estimates are followed up manually and inconsistently. That creates a risk that interested prospects go quiet simply because no structured follow-up happens at the right time.";
  }
  return "You told us estimates are not consistently followed up. That creates a clear risk that qualified opportunities are lost after the estimate simply because the next conversation never happens.";
}

function reviewsContextDetail(answer, result) {
  const rating = typeof result?.map_rating === "number" ? result.map_rating : null;
  const count = typeof result?.map_review_count === "number" ? result.map_review_count : null;
  const observed = [
    rating != null ? `${rating.toFixed(1)}★` : null,
    count != null ? `${count.toLocaleString()} reviews` : null,
  ].filter(Boolean).join(" with ");

  if (answer === "Yes, we dominate") {
    return observed
      ? `You told us reviews are a competitive strength, and RankTrust observed ${observed} on your Google Business Profile. The opportunity is to keep that trust visible throughout the rest of the customer journey.`
      : "You told us reviews are a competitive strength. The opportunity is to keep that trust visible throughout the rest of the customer journey.";
  }
  if (answer === "They help, but we need more") {
    return observed
      ? `You told us your reviews help, but you believe more review strength would improve customer confidence. RankTrust observed ${observed}, so the opportunity may be less about raw volume and more about how that trust is used throughout the customer journey.`
      : "You told us your reviews help, but you believe more review strength would improve customer confidence. That makes review quality, recency, and how trust is displayed worth examining more closely.";
  }
  return observed
    ? `You told us reviews are not currently creating enough confidence. RankTrust observed ${observed}; the next step is to understand whether the issue is rating, recency, review content, or how that trust is presented to customers.`
    : "You told us reviews are not currently creating enough confidence. The next step is to determine whether the gap is rating, recency, review content, volume, or how that trust is presented to customers.";
}

function AssessmentPreview() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="absolute inset-0 bg-gradient-to-br from-[#2D5BFF]/5 via-transparent to-zinc-100/70" />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              RankTrust Assessment Preview
            </div>
            <div className="mt-1 font-display text-lg font-black text-zinc-900">Here&apos;s what we found…</div>
          </div>
          <div className="rounded-full bg-[#2D5BFF] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
            Live signals
          </div>
        </div>
        <div className="mt-5 space-y-3 blur-[2.2px] select-none" aria-hidden="true">
          {[
            ["Google Visibility", "Opportunity detected"],
            ["Website & Answers", "Analysis complete"],
            ["AI Discovery", "Finding detected"],
            ["Competitive Position", "Needs attention"],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
              <span className="text-sm font-semibold text-zinc-700">{label}</span>
              <span className="text-xs font-mono text-zinc-500">{value}</span>
            </div>
          ))}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </div>
    </div>
  );
}

function Progress({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div aria-label={`Question ${current} of ${total}`}>
      <div className="mb-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-zinc-500">
        <span>Question {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-100">
        <div className="h-full bg-[#2D5BFF] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function BusinessMatch({ result }) {
  const name = result?.gbp_business_name || result?.business_name;
  const city = result?.gbp_city || result?.city;
  const state = result?.gbp_state || result?.state;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm" data-testid="check-v2-business-match">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-emerald-50 p-2 text-emerald-700">
          <CheckCircle size={20} weight="fill" />
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Business match</div>
          <h2 className="mt-1 font-display text-2xl font-black text-zinc-900">We found {name}.</h2>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-600">
            {(city || state) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={15} weight="fill" /> {[city, state].filter(Boolean).join(", ")}
              </span>
            )}
            {result?.gbp_primary_category && <span>{result.gbp_primary_category}</span>}
            {result?.verification_status === "verified" && (
              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                <ShieldCheck size={15} weight="fill" /> Google Business Profile verified
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function OpportunityRow({ label, value, source, tone = "blue" }) {
  return (
    <div className="grid gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <div className="font-display font-bold text-zinc-900">{label}</div>
        <div className="mt-1 text-xs text-zinc-500">{source}</div>
      </div>
      <span className={`inline-flex w-fit items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${toneClasses(tone)}`}>
        {value}
      </span>
    </div>
  );
}

export default function PublicAssessmentV2() {
  const [form, setForm] = useState({ business_name: "", keyword: "", city: "", state: "" });
  const [phase, setPhase] = useState("start");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [challenges, setChallenges] = useState([]);
  const [otherChallenge, setOtherChallenge] = useState("");
  const [fullName, setFullName] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [callbackPhone, setCallbackPhone] = useState("");
  const [savingNextStep, setSavingNextStep] = useState(false);
  const [nextStepSaved, setNextStepSaved] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const [candidatePickerOpen, setCandidatePickerOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pickerSubmitting, setPickerSubmitting] = useState(false);

  const phoneEntered = phone.trim().length > 0;
  const signals = useMemo(() => signalStatus(result), [result]);

  const quizStatuses = useMemo(() => ({
    reviews: statusFromAnswer(answers.reviews_trust, "Yes, we dominate", "They help, but we need more"),
    response: statusFromAnswer(answers.lead_response, "Yes, instantly", "Usually within an hour"),
    followup: statusFromAnswer(answers.estimate_follow_up, "Yes, automated/tracked", "Sometimes, manually"),
  }), [answers]);

  const runAssessment = async (payload) => {
    setRunning(true);
    setPhase("analyzing");
    try {
      const { data } = await axios.post(`${API}/public/visibility-check`, payload);
      setResult(data);
      setPhase("questions");
      return data;
    } catch (err) {
      setPhase("start");
      toast.error(err?.response?.data?.detail || "Assessment failed — please try again.");
      return null;
    } finally {
      setRunning(false);
    }
  };

  const begin = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.keyword.trim() || !form.city.trim()) {
      toast.error("Business name, service keyword, and city are required.");
      return;
    }

    const payload = {
      ...form,
      lead_phone: "",
      sms_consent_given: false,
      sms_consent_text_version: null,
      sms_consent_timestamp: null,
      sms_consent_method: null,
      sms_consent_source: null,
    };

    setRunning(true);
    setPhase("analyzing");
    try {
      const { data: cand } = await axios.post(`${API}/public/gbp-candidates`, {
        business_name: form.business_name,
        keyword: form.keyword,
        city: form.city,
        state: form.state,
      });

      if (cand?.requires_picker && Array.isArray(cand.candidates) && cand.candidates.length) {
        setCandidates(cand.candidates);
        setPendingPayload({ ...payload, identity_cache_id: cand.cache_id || null });
        setCandidatePickerOpen(true);
        setRunning(false);
        return;
      }

      if (Array.isArray(cand?.candidates) && cand.candidates.length) {
        payload.confirmed_place_id = cand.candidates[0].place_id;
        payload.identity_cache_id = cand.cache_id || null;
      }
    } catch (_err) {
      // Candidate discovery is a helpful pre-flight only; the assessment
      // endpoint retains its own identity-resolution fallback.
    }

    await runAssessment(payload);
  };

  const chooseCandidate = async (placeId) => {
    if (!pendingPayload || pickerSubmitting) return;
    setPickerSubmitting(true);
    setCandidatePickerOpen(false);
    const payload = { ...pendingPayload, confirmed_place_id: placeId };
    setPendingPayload(null);
    await runAssessment(payload);
    setPickerSubmitting(false);
  };

  const continueWithoutCandidate = async () => {
    if (!pendingPayload || pickerSubmitting) return;
    setPickerSubmitting(true);
    setCandidatePickerOpen(false);
    const payload = { ...pendingPayload };
    delete payload.confirmed_place_id;
    setPendingPayload(null);
    await runAssessment(payload);
    setPickerSubmitting(false);
  };

  const cancelPicker = () => {
    setCandidatePickerOpen(false);
    setCandidates([]);
    setPendingPayload(null);
    setRunning(false);
    setPhase("start");
  };

  const chosenChallenges = challenges.map(x => x === "Something else" ? otherChallenge.trim() : x);
  const saveNextStep = async (e) => {
    e.preventDefault();
    if (!nextStep || (nextStep === "managed" && !callbackPhone.trim())) return;
    setSavingNextStep(true);
    try {
      if (nextStep === "guidance") {
        const { data } = await axios.post(`${API}/public/visibility-check/${result.id}/consultation/checkout`, {
          email: email.trim(),
        });
        if (!data?.checkout_url?.startsWith("https://")) {
          throw new Error("Secure checkout is unavailable.");
        }
        window.location.assign(data.checkout_url);
        return;
      }
      await axios.post(`${API}/public/visibility-check/${result.id}/next-step`, {
        email: email.trim(), choice: nextStep,
        callback_phone: nextStep === "managed" ? callbackPhone.trim() : null,
      });
      setNextStepSaved(true);
      toast.success(nextStep === "managed" ? "Your callback request is saved." : "Your preference is saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your choice. Please retry.");
    } finally {
      setSavingNextStep(false);
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { toast.error("Enter your full name."); return; }
    if (!email.trim()) {
      toast.error("Enter your email to receive the complete assessment.");
      return;
    }
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API}/public/visibility-check/${result.id}/unlock`, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        sms_consent_given: phoneEntered && smsConsent,
        sms_consent_timestamp: phoneEntered && smsConsent ? new Date().toISOString() : null,
        sms_consent_text_version: phoneEntered && smsConsent ? SMS_CONSENT_TEXT_VERSION : null,
        sms_consent_method: phoneEntered && smsConsent ? "checked_checkbox" : null,
        sms_consent_source: phoneEntered && smsConsent ? "ranktrust_check_unlock" : null,
        context_answers: { biggest_challenges: chosenChallenges.join("; ") },
      });
      setResult(data);
      setPhase("results");
      toast.success("Your complete assessment is ready.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't open the complete assessment. Please try again.");
    } finally {
      setUnlocking(false);
    }
  };

  const reset = () => {
    setPhase("start");
    setRunning(false);
    setResult(null);
    setQuestionIndex(0);
    setAnswers({});
    setChallenges([]); setOtherChallenge(""); setFullName("");
    setNextStep(""); setCallbackPhone(""); setNextStepSaved(false); setSavingNextStep(false);
    setEmail("");
    setPhone("");
    setSmsConsent(false);
  };

  const observedFindings = [
    observedGoogleFinding(result),
    observedWebsiteFinding(result),
    observedAiFinding(result),
    observedReviewFinding(result),
  ].filter(Boolean);

  const contextFindings = []; // Internal processes are not inferred from public data.

  const allFindings = [...observedFindings, ...contextFindings];
  const resultsPriority = [...allFindings]
    .filter((item) => item.severity > 0)
    .sort((a, b) => (b.severity - a.severity) || (Number(b.observed) - Number(a.observed)))
    .slice(0, 3);

  const topPriority = resultsPriority[0];
  const measuredAiProviders = Object.entries(result?.engines || {})
    .filter(([, data]) => data && !data.mocked && typeof data.mention_rate === "number");
  const executiveDiagnosis =
    topPriority?.key === "ai" && measuredAiProviders.length === 1
      ? `When customers ask ${engineDisplayName(measuredAiProviders[0][0])} who to hire${result?.city ? ` in ${result.city}` : ""}, your business ${Number(measuredAiProviders[0][1].mention_rate) === 0 ? "did not appear in the recommendation tests we ran" : "appeared inconsistently in the recommendation tests we ran"}.`
      : topPriority?.key === "ai" && measuredAiProviders.length > 1
        ? `When customers ask AI systems who to hire${result?.city ? ` in ${result.city}` : ""}, your business ${topPriority.value === "0% mention rate" ? "did not appear in the recommendation tests we ran" : "appeared inconsistently in the recommendation tests we ran"}.`
        : topPriority?.detail ||
          result?.score_summary ||
          "RankTrust completed the available live checks. Internal processes require confirmation.";

  return (
    <div className="min-h-screen bg-zinc-50">
      <SEOHead path="/check-v2" />
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5 lg:px-10">
          <Link to="/" className="block">
            <img
              src="https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/mlg09j9p_Ranktrust%20new%20logo.png"
              alt="RankTrust"
              style={{ height: 36, width: "auto", display: "block" }}
            />
            <div className="mt-1 hidden whitespace-nowrap text-[10px] font-semibold uppercase tracking-[1.5px] text-zinc-500 sm:block">
              The Business Growth Intelligence Platform
            </div>
          </Link>
          <div className="hidden text-xs font-mono text-zinc-500 sm:block">No account required</div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 lg:px-10 lg:py-14">
        {phase === "start" && (
          <>
            <div className="grid gap-10 lg:grid-cols-[1.05fr_.95fr] lg:items-start">
              <div>
                <div className="label-tiny mb-3 flex items-center gap-2">
                  <Robot size={14} weight="bold" /> Free Service Business Assessment
                </div>
                <h1 className="font-display text-4xl font-black tracking-tight text-zinc-950 sm:text-5xl lg:text-6xl lg:leading-[0.95]">
                  Get Your Service Business Assessment
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-600 lg:text-lg">
                  Discover where your business may be missing visibility, customer confidence, and revenue across Google, your website, AI answers, and the competitive journey.
                </p>

                <form onSubmit={begin} className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm sm:p-6" data-testid="check-v2-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="sm:col-span-2">
                      <span className="label-tiny mb-1 block">Business Name</span>
                      <input
                        className="input-field w-full"
                        value={form.business_name}
                        onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                        placeholder="e.g. Cumberland Mechanical"
                        required
                        data-testid="check-v2-business-name"
                      />
                    </label>
                    <label>
                      <span className="label-tiny mb-1 block">Service / Search Keyword</span>
                      <input
                        className="input-field w-full"
                        value={form.keyword}
                        onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                        placeholder="e.g. AC repair"
                        required
                        data-testid="check-v2-keyword"
                      />
                    </label>
                    <div className="grid grid-cols-[1fr_88px] gap-2">
                      <label>
                        <span className="label-tiny mb-1 block">City / Market</span>
                        <input
                          className="input-field w-full"
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          placeholder="Nashville"
                          required
                          data-testid="check-v2-city"
                        />
                      </label>
                      <label>
                        <span className="label-tiny mb-1 block">State</span>
                        <input
                          className="input-field w-full"
                          value={form.state}
                          onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                          placeholder="TN"
                          maxLength={3}
                          data-testid="check-v2-state"
                        />
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={running} className="btn-primary mt-5 w-full sm:w-auto" data-testid="check-v2-analyze">
                    <Sparkle size={15} weight="fill" /> ANALYZE MY BUSINESS <ArrowRight size={14} weight="bold" />
                  </button>
                  <div className="mt-3 text-xs text-zinc-500">No email or phone required to start.</div>
                </form>
              </div>

              <div className="space-y-5">
                <AssessmentPreview />
                <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Your assessment examines</div>
                  <ul className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-700">
                    {[
                      "Where customers may be finding competitors first",
                      "How your website helps or hurts customer confidence",
                      "Whether AI systems understand and surface your business",
                      "Where opportunities may be falling through the customer journey",
                    ].map((item) => (
                      <li key={item} className="flex gap-2.5">
                        <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-[#2D5BFF]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {phase === "analyzing" && (
          <div className="mx-auto max-w-2xl py-16 text-center" data-testid="check-v2-analyzing">
            <CircleNotch size={34} className="mx-auto animate-spin text-[#2D5BFF]" />
            <h1 className="mt-6 font-display text-3xl font-black text-zinc-950 sm:text-4xl">
              Analyzing {form.business_name}…
            </h1>
            <p className="mt-3 text-zinc-600">
              Checking local visibility, competitive position, website signals, and AI visibility.
            </p>
            <div className="mx-auto mt-8 h-1.5 max-w-md overflow-hidden rounded-full bg-zinc-200">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-[#2D5BFF]" />
            </div>
          </div>
        )}

        {phase === "questions" && result && (
          <div className="mx-auto max-w-3xl space-y-6" data-testid="check-v2-challenges">
            <BusinessMatch result={result} />
            <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="text-xs font-mono uppercase text-[#2D5BFF]">Your Business Priorities</div>
              <h2 className="mt-3 font-display text-3xl font-black">What's the biggest challenge you're trying to solve right now?</h2>
              <p className="mt-2 text-sm text-zinc-600">Select up to two. Your full assessment will still include all available verified findings.</p>
              <div className="mt-6 grid gap-2">
                {CHALLENGES.map(challenge => (
                  <label key={challenge} className="flex cursor-pointer gap-3 rounded-xl border border-zinc-200 p-4 text-sm font-semibold">
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#2D5BFF]"
                      checked={challenges.includes(challenge)}
                      disabled={!challenges.includes(challenge) && challenges.length >= 2}
                      onChange={e => setChallenges(prev => e.target.checked ? [...prev, challenge] : prev.filter(x => x !== challenge))} />
                    {challenge}
                  </label>
                ))}
                {challenges.includes("Something else") && (
                  <textarea className="input-field w-full" rows={2} maxLength={240}
                    value={otherChallenge} onChange={e => setOtherChallenge(e.target.value)}
                    placeholder="Tell us what you are trying to solve" aria-label="Other challenge" />
                )}
              </div>
              <button type="button" className="btn-primary mt-6" data-testid="check-v2-challenges-continue"
                disabled={!challenges.length || (challenges.includes("Something else") && !otherChallenge.trim())}
                onClick={() => { setPhase("opportunity"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                Continue <ArrowRight size={14} weight="bold" />
              </button>
            </section>
          </div>
        )}

        {phase === "opportunity" && result && (
          <div className="mx-auto max-w-3xl space-y-6" data-testid="check-v2-opportunity">
            <BusinessMatch result={result} />
            <section className="rounded-2xl border border-[#2D5BFF]/30 bg-white p-6 shadow-sm sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-800">
                <WarningCircle size={14} weight="fill" /> Opportunity Alert
              </div>
              <h1 className="mt-4 font-display text-3xl font-black tracking-tight text-zinc-950 sm:text-4xl">
                Your Business Generated an Opportunity Alert
              </h1>
              <p className="mt-3 text-zinc-600">
                We found areas worth examining across your visibility and competitive journey.
              </p>

              <div className="mt-7 space-y-3">
                <OpportunityRow
                  label="Google Visibility"
                  value={signals.hasMapFinding ? "Finding detected" : "Analysis complete"}
                  source="RankTrust observed"
                  tone="blue"
                />
                <OpportunityRow
                  label="Website & Customer Answers"
                  value={signals.hasWebsiteFinding ? "Finding detected" : "Analysis complete"}
                  source="RankTrust observed"
                  tone="blue"
                />
                <OpportunityRow
                  label="Reviews & Trust"
                  value={observedReviewFinding(result)?.value || "Not measured"}
                  source="Public review data"
                  tone={observedReviewFinding(result)?.tone || "zinc"}
                />
                <OpportunityRow
                  label="Lead Response"
                  value="Not assessed"
                  source="Requires internal business data"
                  tone="zinc"
                />
                <OpportunityRow
                  label="AI Answers"
                  value={signals.hasAiFinding ? "Analysis complete" : "Analysis complete"}
                  source="RankTrust observed"
                  tone="blue"
                />
                <OpportunityRow
                  label="Estimate Follow-Up"
                  value="Not assessed"
                  source="Requires internal business data"
                  tone="zinc"
                />
              </div>

              <div className="mt-8 border-t border-zinc-200 pt-7">
                <h2 className="font-display text-2xl font-black text-zinc-950">
                  Where should we send your complete Service Business Assessment?
                </h2>
                <form onSubmit={unlock} className="mt-5 space-y-4">
                  <label className="block"><span className="label-tiny mb-1 block">Full Name</span>
                    <input className="input-field w-full" type="text" autoComplete="name" required maxLength={120}
                      value={fullName} onChange={e => setFullName(e.target.value)}
                      placeholder="Your full name" data-testid="check-v2-full-name" />
                  </label>
                  <label className="block">
                    <span className="label-tiny mb-1 block">Email Address</span>
                    <input
                      type="email"
                      className="input-field w-full"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@business.com"
                      required
                      data-testid="check-v2-email"
                    />
                  </label>
                  <label className="block">
                    <span className="label-tiny mb-1 block">
                      Want your assessment and follow-up by text too? <span className="font-normal text-zinc-400">Optional</span>
                    </span>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      className="input-field w-full"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (!e.target.value.trim()) setSmsConsent(false);
                      }}
                      placeholder="(706) 555-1234"
                      data-testid="check-v2-phone"
                    />
                  </label>

                  {phoneEntered && (
                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4" data-testid="check-v2-sms-consent-block">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-zinc-300 text-[#2D5BFF] focus:ring-[#2D5BFF]"
                          data-testid="check-v2-sms-consent"
                        />
                        <span className="text-xs leading-relaxed text-zinc-700">
                          By checking this box, you agree to receive recurring SMS messages from <strong>RankTrust</strong>, operated by Card Service Warehouse, Inc. regarding your Business Growth Assessment, assessment results, appointment scheduling, account updates, and related customer service communications. Message frequency varies but will not exceed 5 messages per month. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is voluntary and is not a condition of purchase. View our{" "}
                          <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a> and{" "}
                          <a href="/privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.
                        </span>
                      </label>
                    </div>
                  )}

                  <button type="submit" disabled={unlocking} className="btn-primary w-full sm:w-auto" data-testid="check-v2-unlock">
                    {unlocking ? (
                      <><CircleNotch size={15} className="animate-spin" /> Preparing your assessment…</>
                    ) : (
                      <><EnvelopeSimple size={15} weight="bold" /> GET MY COMPLETE ASSESSMENT <ArrowRight size={14} weight="bold" /></>
                    )}
                  </button>
                  <p className="text-xs leading-relaxed text-zinc-500">
                    Mobile is optional. SMS consent appears only when a mobile number is entered.
                  </p>
                </form>
              </div>
            </section>
          </div>
        )}

        {phase === "results" && result && (
          <div className="mx-auto max-w-4xl space-y-6" data-testid="check-v2-results">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#2D5BFF]">Your Service Business Assessment</div>
            <div>
              <h1 className="font-display text-4xl font-black tracking-tight text-zinc-950 sm:text-5xl">Here&apos;s what we found…</h1>
              <p className="mt-3 text-zinc-600">
                {result.gbp_business_name || result.business_name} · {[result.city, result.state].filter(Boolean).join(", ")}
              </p>
            </div>

            <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Executive Summary</div>
              <h2 className="mt-2 font-display text-2xl font-black text-zinc-950">Start with what deserves attention first.</h2>
              <p className="mt-3 max-w-3xl text-base font-semibold leading-relaxed text-zinc-900">
                {executiveDiagnosis}
              </p>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-600">
                RankTrust reports verified public signals where available. Internal business processes cannot be confirmed from public information.
              </p>
              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm" data-testid="check-v2-interest-summary"><strong>Your priorities:</strong> {chosenChallenges.join("; ")}</div>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {(resultsPriority.length ? resultsPriority : allFindings.slice(0, 3)).map((item, idx) => (
                  <div key={item.key || item.label} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Priority {idx + 1}</div>
                    <div className="mt-1 font-display font-bold text-zinc-900">{item.label}</div>
                    <span className={`mt-3 inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${toneClasses(item.tone)}`}>
                      {item.value}
                    </span>
                    <p className="mt-3 text-xs leading-relaxed text-zinc-600">{item.detail}</p>
                    <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">{item.source}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="font-display text-2xl font-black text-zinc-950">What RankTrust Found</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-zinc-600">
                These are the specific public signals RankTrust was able to verify during this assessment.
              </p>
              <div className="mt-5 space-y-3">
                {observedFindings.length ? observedFindings.map((item) => (
                  <div key={item.key} className="rounded-xl border border-zinc-200 bg-white px-4 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="font-display font-bold text-zinc-900">{item.label}</div>
                        <div className="mt-1 text-xs font-mono uppercase tracking-wider text-zinc-400">{item.source}</div>
                      </div>
                      <span className={`inline-flex w-fit items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${toneClasses(item.tone)}`}>
                        {item.value}
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-600">{item.detail}</p>
                  </div>
                )) : (
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-4 text-sm text-zinc-600">
                    No specific live public finding was available for this run. RankTrust will not substitute an assumption for a measured result.
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm sm:p-8" data-testid="check-v2-next-step">
              <div className="text-xs font-mono uppercase text-[#2D5BFF]">Your Next Step</div>
              <h2 className="mt-2 font-display text-3xl font-black">How would you like to move forward?</h2>
              {nextStepSaved ? (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                  <strong>{nextStep === "managed" ? "Your callback request is saved." : "Your preference is saved."}</strong>
                  <p className="mt-2 text-sm">{nextStep === "managed" ? "RankTrust has your callback number and assessment details." : "You can review your assessment whenever you're ready."}</p>
                </div>
              ) : (
                <form onSubmit={saveNextStep} className="mt-5 space-y-3">
                  {[
                    ["self", "I'll Do It Myself", "Use my assessment and recommendations to make improvements independently. · FREE"],
                    ["guidance", "Do It Myself With Guidance", "$80 / 30-minute consultation. Review my assessment with a RankTrust specialist, get answers, and prioritize improvements."],
                    ["managed", "Have RankTrust Handle It", "Request a personal callback to discuss implementation and pricing."],
                  ].map(([value, label, detail]) => (
                    <label key={value} className={`flex cursor-pointer gap-3 rounded-xl border p-4 ${value === "guidance" ? "border-blue-300 bg-blue-50" : "border-zinc-200"}`}>
                      <input type="radio" name="next-step" value={value} checked={nextStep === value}
                        onChange={() => setNextStep(value)} className="mt-1 accent-[#2D5BFF]" />
                      <span className="text-sm"><strong className="block">{label}</strong>
                        {value === "guidance" && <span className="mt-1 block text-lg font-black text-[#2D5BFF]">$80 <small className="text-xs font-semibold text-zinc-600">for 30 minutes</small></span>}
                        <span className="mt-1 block text-zinc-600">{detail}</span>
                      </span>
                    </label>
                  ))}
                  {nextStep === "managed" && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <h3 className="font-bold">Where should RankTrust call you?</h3>
                      <p className="mt-1 text-sm text-zinc-700">Your callback request goes directly to David at david@ranktrust.io.</p>
                      <p className="mt-1 text-xs text-zinc-500">We will use your email from the assessment to send any requested scheduling information.</p>
                      <label className="mt-3 block text-sm font-semibold">Best callback number
                        <input type="tel" className="input-field mt-1 w-full bg-white" required autoComplete="tel"
                          value={callbackPhone} onChange={e => setCallbackPhone(e.target.value)}
                          placeholder="(706) 555-1234" data-testid="check-v2-callback-phone" />
                      </label>
                      <p className="mt-3 text-xs text-zinc-600">
                        By requesting a callback, I agree to receive a personal phone call from RankTrust regarding my business assessment.
                        This does not opt me in to SMS or automated calls.
                      </p>
                    </div>
                  )}
                  <button type="submit" disabled={!nextStep || savingNextStep} className="btn-primary w-full sm:w-auto"
                    data-testid="check-v2-next-step-submit">
                    {savingNextStep ? "Preparing…" : nextStep === "guidance" ? "Book & Pay for My Consultation · $80" : nextStep === "managed" ? "Request My Callback" : "Save My Preference"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                </form>
              )}
            </section>

            <section className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-7">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <img src="/service-business-opportunity-playbook-cover.jpg"
                  alt="The Service Business Opportunity Playbook" className="w-24 rounded-md shadow" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-[#2D5BFF]">Keep Exploring</div>
                  <h2 className="mt-1 font-display text-xl font-bold">The Service Business Opportunity Playbook</h2>
                  <p className="mt-2 text-sm text-zinc-600">Use your assessment to understand where your next growth opportunities may be.</p>
                  <Link to={`/check?cid=${encodeURIComponent(result.id)}`}
                    className="mt-3 inline-block text-sm font-bold text-[#2D5BFF] underline">
                    View Full Detailed Assessment
                  </Link>
                </div>
              </div>
            </section>

            <button type="button" onClick={reset} className="text-sm font-mono text-zinc-500 underline underline-offset-4 hover:text-zinc-900">
              ← Run another assessment
            </button>
          </div>
        )}

        <div className="mt-16 border-t border-zinc-200 pt-7 text-xs leading-relaxed text-zinc-500">
          RankTrust distinguishes observed public signals from information you provide about your own operations. Recommendations should be based on verified findings where possible and clearly labeled context where they are not.
        </div>
      </main>

      {candidatePickerOpen && (
        <GbpCandidatePicker
          candidates={candidates}
          onSelect={chooseCandidate}
          onNoneMatch={continueWithoutCandidate}
          onCancel={cancelPicker}
          loading={pickerSubmitting}
        />
      )}
    </div>
  );
}
